import { create } from 'zustand';
import { useCallback, useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';

type DataRow = Record<string, unknown>;

interface ComponentDataState {
  data: Record<string, DataRow[]>;
  loading: Record<string, boolean>;
  errors: Record<string, string | null>;
  setData: (componentId: string, records: DataRow[]) => void;
  setLoading: (componentId: string, loading: boolean) => void;
  setError: (componentId: string, error: string | null) => void;
  clearData: (componentId: string) => void;
}

const EMPTY_ARRAY: DataRow[] = [];

export const useComponentDataStore = create<ComponentDataState>((set) => ({
  data: {},
  loading: {},
  errors: {},

  setData: (componentId, records) =>
    set((state) => ({
      data: { ...state.data, [componentId]: records },
      loading: { ...state.loading, [componentId]: false },
      errors: { ...state.errors, [componentId]: null },
    })),

  setLoading: (componentId, isLoading) =>
    set((state) => ({
      loading: { ...state.loading, [componentId]: isLoading },
    })),

  setError: (componentId, error) =>
    set((state) => ({
      errors: { ...state.errors, [componentId]: error },
      loading: { ...state.loading, [componentId]: false },
    })),

  clearData: (componentId) =>
    set((state) => {
      const { [componentId]: _d, ...restData } = state.data;
      const { [componentId]: _e, ...restErrors } = state.errors;
      const { [componentId]: _l, ...restLoading } = state.loading;
      return { data: restData, errors: restErrors, loading: restLoading };
    }),
}));

export function useComponentData(componentId: string) {
  const dataSelector = useCallback((s: ComponentDataState) => s.data[componentId] ?? EMPTY_ARRAY, [componentId]);
  const loadingSelector = useCallback((s: ComponentDataState) => s.loading[componentId] ?? false, [componentId]);
  const errorSelector = useCallback((s: ComponentDataState) => s.errors[componentId] ?? null, [componentId]);

  const data = useComponentDataStore(dataSelector);
  const loading = useComponentDataStore(loadingSelector);
  const error = useComponentDataStore(errorSelector);
  return { data, loading, error };
}

/**
 * Returns query data for multiple component IDs (for computed expression dependencies).
 * Uses shallow equality to prevent re-renders when data references haven't changed.
 */
export function useMultiComponentData(
  componentIds: string[]
): Record<string, DataRow[]> {
  const idsKey = useMemo(() => componentIds.join(','), [componentIds]);

  const selector = useCallback(
    (s: ComponentDataState) => {
      const result: Record<string, DataRow[]> = {};
      for (const id of componentIds) {
        if (id in s.data) result[id] = s.data[id];
      }
      return result;
    },
    [componentIds, idsKey]
  );

  return useComponentDataStore(useShallow(selector));
}

/**
 * Returns true when all dependency components have completed fetching (even with empty data).
 * Used by computed expressions to delay evaluation until all references have resolved.
 */
export function useAllDependenciesLoaded(componentIds: string[]): boolean {
  const idsKey = useMemo(() => componentIds.join(','), [componentIds]);

  const selector = useCallback(
    (s: ComponentDataState) =>
      componentIds.length > 0 &&
      componentIds.every(id => id in s.data && !s.loading[id]),
    [componentIds, idsKey]
  );

  return useComponentDataStore(selector);
}
